/**
 * Customer-side product image carousel. Single translate-X track so
 * swipes / dot taps / auto-advance all share one animation — avoids the
 * old stacked-slides setup where a slide-in + opacity-crossfade ran in
 * parallel ("scrolling then splash"). RTL-aware: swipe direction and
 * dot order both flip with reading direction.
 *
 * Public exports kept for tests:
 *   - `swipeDeltaToIndexStep(dx)`
 *   - `computeVisibleDots(total, activeIndex)`
 *
 * ── AND A MOUSE CAN MOVE IT NOW ──────────────────────────────────────────
 *
 * The owner: "I can't scroll images of products in pc in any page." Measured
 * at 1440 in Chromium on dk-store, `he` and `en` alike: a wheel over a card
 * does nothing on either axis, a mouse drag does nothing and ends on the
 * product page, and clicking a dot — the card's only pointer control —
 * NAVIGATED instead of changing the picture (`location.pathname` became
 * `/dk-store/p/…`, the active dot went 0 → 0). The dot measured 4×4 px.
 *
 * Nothing here forwards the wheel: `utils/forwardVerticalWheel.js` and
 * `Pages/Shop/__tests__/wheelIsNeverTrapped.test.js` exist because consuming
 * `deltaY` over a strip was tried once and cost 25% of every page scroll, and
 * `deltaX` buys a mouse nothing. Drag is not added either — the carousel sits
 * inside a react-router `<Link>`, so it would need `draggable={false}` plus a
 * click-swallowing ref plus mouse-only event names or every phone swipe steps
 * twice. Arrows, like the reference and like the rest of this page.
 */
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FiChevronLeft, FiChevronRight, FiPlay } from 'react-icons/fi';
import { isVideoUrl } from '../../utils/mediaType';
import { renditionSrcSet } from '../../utils/imageRenditions';
import PinchZoomImage from '../PinchZoomImage';
import styles from './styles.module.css';

const MAX_VISIBLE_DOTS = 5;
const DEFAULT_AUTO_SLIDE_MS = 3500;
/** How much of the finger's travel the track follows past the first or the last photo. */
const EDGE_PULL = 0.35;

/** Physical swipe: left → next image, right → previous (same in LTR and RTL). */
export function swipeDeltaToIndexStep(dx) {
  return dx < 0 ? 1 : -1;
}

/**
 * THE EDGES OF THE SCREEN BELONG TO THE PHONE.
 *
 * Instagram's and Facebook's in-app browsers read a swipe that starts at the screen's edge as
 * "back" (and "forward"), and the OS has it before the page sees a second touch event — the owner
 * swiped through the photographs of a product from a Facebook ad and the page slid out to Facebook
 * (utils/inAppBackGuard gives that gesture somewhere to go). A touch that starts in that strip is
 * not a swipe of ours: the track does not follow it, and is not left mid-drag when the OS takes it.
 * 24px is wider than iOS's edge region and Android's default, on any phone the shop is opened on.
 */
export const EDGE_GESTURE_PX = 24;

/** Whether a touch at `clientX` starts within the strip the OS reads as its own gesture. */
export function touchStartsAtScreenEdge(clientX, viewportWidth) {
  if (typeof clientX !== 'number' || !(viewportWidth > 0)) return false;
  return clientX <= EDGE_GESTURE_PX || clientX >= viewportWidth - EDGE_GESTURE_PX;
}

/** Append a tiny media fragment so iOS Safari paints a first frame as a poster
 *  even when the video isn't autoplaying (cards) or is still off-screen. The
 *  fragment is never sent to the server, so it's safe with signed URLs. No-op
 *  when the URL already carries a fragment. */
function posterFrameSrc(url) {
  if (!url || url.includes('#')) return url;
  return `${url}#t=0.1`;
}

function detectRtl() {
  if (typeof document === 'undefined') return false;
  return document.documentElement?.dir === 'rtl' || document.body?.dir === 'rtl';
}

const ProductCarousel = ({
  images,
  autoSlide = false,
  autoSlideMs = DEFAULT_AUTO_SLIDE_MS,
  alt = '',
  peekHint = false,
  counter = false,
  index: controlledIndex,
  onIndexChange,
  /** The first photograph has arrived — what else the page wants to fetch may go now (Pages/ProductDetail). */
  onFirstPhotoLoad,
  hideOverlays = false,
  fitContain = false,
  /* THE PICTURE IN ITS OWN BOX (Swe Sosetta run 4, round B: S4 — fix round 2). The product page's stage paints a blurred
   * copy of the shown picture behind the carousel, so a picture that does not fill the frame has no white bands. A
   * see-through cut-out must not show its own ghost through itself, so each picture is drawn in a box of its OWN
   * aspect ratio (naturalWidth / naturalHeight once loaded, the payload's width / height before that when it has
   * them), as large as the frame allows and centred, and that box carries the plain surface
   * (`--carousel-picture-surface`): the blur only ever shows in the bands AROUND the picture. Never `object-fit:
   * contain` inside a full-size box, which would put the surface (or the blur) behind the whole frame. */
  framed = false,
  /** `framed`: told the address of a picture that has no shape of its own (an SVG with no intrinsic size, a picture
   *  that failed to load) — it is drawn on the plain frame, and the page puts no blurred copy behind it. */
  onPictureUnsized,
  transparentBg = false,
  videoAutoplay = false,
  videoTapToggle = false,
  zoomable = false,
  desktopNav,
  /* How wide the picture is drawn, as a `sizes` value. Given → a rendition
   * URL gets a `srcset` and the browser fetches the card-sized sibling
   * (`utils/imageRenditions`). Omitted → the full picture, which is what the
   * gallery and the zoom viewer want. */
  sizes,
  /* Told of every photo change the SHOPPER made — a swipe, an arrow, a dot —
   * as `(index, action)`, and never of autoplay or a reset to the first photo
   * when the pictures change. The journey of a visit from an ad reads it
   * (utils/shopJourney.js). */
  onShopperMove,
}) => {
  // The dots are the only thing on a product card that speaks to a screen
  // reader, and they were speaking ENGLISH on a Hebrew and an Arabic page
  // ("Image 2 of 3"), because the labels were string literals rather than
  // keys. A shopper using VoiceOver in Arabic heard the one untranslated
  // sentence on the page.
  const { t } = useTranslation();
  const safe = Array.isArray(images) ? images : [];
  const [internalIndex, setInternalIndex] = useState(0);
  const isControlled = controlledIndex != null;
  const index = isControlled ? controlledIndex : internalIndex;
  const setIndex = (next) => {
    const value = typeof next === 'function' ? next(index) : next;
    if (!isControlled) setInternalIndex(value);
    if (onIndexChange) onIndexChange(value);
  };
  const [hovered, setHovered] = useState(false);
  const [hintDone, setHintDone] = useState(() => !peekHint);
  const [dragDx, setDragDx] = useState(0);
  /* The slides whose photographs have been asked for: the first, then each one the shopper came within a step of. */
  const [wanted, setWanted] = useState(() => new Set([0]));
  /* The neighbour is asked for once the first photograph has arrived — never beside it on the line. */
  const wantNeighbours = () => {
    setWanted((prev) => (prev.has(1) ? prev : new Set([...prev, 1])));
    if (onFirstPhotoLoad) onFirstPhotoLoad();
  };
  const firstIndexRef = useRef(true);
  useEffect(() => {
    // At mount only the first photograph is asked for; the neighbours wait for it to arrive (onLoad above).
    if (firstIndexRef.current) { firstIndexRef.current = false; return; }
    setWanted((prev) => {
      const next = [index - 1, index, index + 1].filter((i) => i >= 0 && !prev.has(i));
      if (!next.length) return prev;
      const grown = new Set(prev);
      next.forEach((i) => grown.add(i));
      return grown;
    });
  }, [index]);
  const touchStart = useRef({ x: null, y: null, w: 0, tracking: false, axis: null });
  const wrapRef = useRef(null);
  /* `framed`: the frame's size, measured before paint and on every resize, and each picture's own aspect ratio.
   *
   * The wrap is held by a CALLBACK ref too (fix round 3): a carousel that mounts with no pictures renders a placeholder
   * and no wrap, and an effect keyed on `framed` alone would never measure the wrap that arrives with the pictures. */
  const [frame, setFrame] = useState(null);
  const [ratios, setRatios] = useState({});
  const [wrapEl, setWrapEl] = useState(null);
  const attachWrap = (el) => {
    wrapRef.current = el;
    if (el !== wrapEl) setWrapEl(el);
  };
  const measureWrap = (el = wrapRef.current) => {
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (w > 0 && h > 0) setFrame((f) => (f && f.w === w && f.h === h ? f : { w, h }));
  };
  useLayoutEffect(() => {
    if (!framed) return undefined;
    const el = wrapEl;
    if (!el) return undefined;
    const measure = () => measureWrap(el);
    measure();
    if (typeof ResizeObserver !== 'function') {
      window.addEventListener('resize', measure);
      return () => window.removeEventListener('resize', measure);
    }
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [framed, wrapEl]);
  /** The picture's own width / height: measured once it has loaded, else what the payload says, else unknown. */
  const ratioOf = (img) => {
    if (ratios[img.url] === 'none') return null;
    if (ratios[img.url]) return ratios[img.url];
    const w = Number(img.width);
    const h = Number(img.height);
    return w > 0 && h > 0 ? w / h : null;
  };
  /** The picture's box in the frame: as large as fits, its own shape. Null until both are known (the full frame then). */
  const boxOf = (img) => {
    const ar = framed && frame ? ratioOf(img) : null;
    if (!ar) return null;
    return { width: Math.round(Math.min(frame.w, frame.h * ar)), height: Math.round(Math.min(frame.h, frame.w / ar)) };
  };
  /** A picture with no shape of its own (fix round 3): an SVG with no intrinsic size, or one that did not load. */
  const unsized = (img) => framed && ratios[img.url] === 'none';
  const markUnsized = (img) => {
    if (!framed) return;
    setRatios((r) => (r[img.url] === 'none' ? r : { ...r, [img.url]: 'none' }));
    onPictureUnsized?.(img.url);
  };
  const noteRatio = (img, el) => {
    if (!framed) return;
    // A frame that could not be measured before (no layout yet, no ResizeObserver) is measured now.
    measureWrap();
    const w = el?.naturalWidth;
    const h = el?.naturalHeight;
    if (!(w > 0) || !(h > 0)) { markUnsized(img); return; }
    setRatios((r) => (r[img.url] === w / h ? r : { ...r, [img.url]: w / h }));
  };
  /** framed: 'sized' (its own box), 'unsized' (the plain frame), else 'pending' — hidden until it is one of the two. */
  const framedState = (img) => {
    if (!framed) return undefined;
    if (unsized(img)) return 'unsized';
    return boxOf(img) ? 'sized' : 'pending';
  };
  /* THE FINGER'S MOVE IS READ BY A LISTENER THE BROWSER WAITS FOR (2026-10-06).
   *
   * The owner, iPhone: "when I scroll pics in the pics area, it moves a bit up or down — it's like not focused
   * on scrolling pics but also allows vertical scrolling at the same time." `.wrap` says `touch-action: pan-y`
   * — the page may scroll vertically from a finger on the photographs, which it must, since on a phone the
   * stage is most of the first screen — and Safari decides for itself, from the first few pixels, whether a
   * diagonal swipe is a vertical pan. When it says yes and this handler says no (`|dx| > |dy|`), both ran: the
   * track followed the finger and the page crept. React registers `touchmove` as a passive listener, so
   * `preventDefault()` from an `onTouchMove` prop is ignored — this is a native one, `passive: false`, which
   * makes the browser wait for it; once the gesture is locked to the x axis (`touchStart.current.axis`) every
   * move is prevented and the page stays put. A gesture locked to y is the page's, and the track lets go.
   * The handler itself is read through a ref so the listener is attached once and never stale. */
  const touchMoveRef = useRef(null);
  useEffect(() => {
    const el = wrapRef.current;
    if (!el || safe.length <= 1) return undefined;
    const onMove = (e) => {
      if (touchMoveRef.current) touchMoveRef.current(e);
      if (touchStart.current.axis === 'x' && e.cancelable) e.preventDefault();
    };
    el.addEventListener('touchmove', onMove, { passive: false });
    return () => el.removeEventListener('touchmove', onMove);
  }, [safe.length]);
  const videoRefs = useRef({});

  /* BACK TO THE FIRST SLIDE WHEN THE PICTURES CHANGE — BUT NOT ON ARRIVAL.
   *
   * When the list of images is swapped under a mounted carousel (a card
   * re-used for another product, a variation picked), an index left over from
   * the old list points at the wrong photo or past the end of a shorter one.
   * That is what this is for, and it stays.
   *
   * The FIRST run is different, and it was doing damage. On mount there is
   * nothing left over to correct: an uncontrolled carousel is already at 0, so
   * the write is a no-op — but a CONTROLLED one has a parent holding the
   * index, and `setIndex(0)` calls `onIndexChange(0)`, which is the child
   * ordering its parent back to the start the moment it appears. Two things
   * that cost:
   *
   *   - the full-screen viewer opened on photo 1 whichever photograph you
   *     tapped, because its carousel mounts on open and immediately reset the
   *     index it was handed;
   *   - on the product page, a click on the gallery in the first frames after
   *     the payload landed was silently undone by this effect flushing after
   *     it — with unresized 480KB photographs still decoding, a real window.
   *
   * A ref rather than a `mounted` state so the skip costs no extra render.
   */
  const listSettled = useRef(false);
  useEffect(() => {
    if (!listSettled.current) { listSettled.current = true; return; }
    setIndex(0);
  }, [safe.length, safe[0]?.url]);

  // One-shot peek on the first list card — slide halfway toward the
  // next image and back so customers see the carousel is swipeable.
  useEffect(() => {
    if (!peekHint || safe.length <= 1) {
      setHintDone(true);
      return undefined;
    }
    setHintDone(false);
    const fallback = setTimeout(() => setHintDone(true), 2200);
    return () => clearTimeout(fallback);
  }, [peekHint, safe.length, safe[0]?.url]);

  // Auto-advance: ticks the index, same translate-X transition handles
  // the visual motion. Paused while the peek hint is animating or the
  // user is hovering / dragging.
  useEffect(() => {
    if (!autoSlide || hovered || safe.length <= 1 || !hintDone || dragDx !== 0) return undefined;
    const id = setInterval(() => setIndex((i) => (i + 1) % safe.length), autoSlideMs);
    return () => clearInterval(id);
  }, [autoSlide, hovered, autoSlideMs, safe.length, hintDone, dragDx]);

  // Drive video play/pause imperatively for the ACTIVE slide only. iOS Safari
  // won't reliably autoplay off-screen <video> elements, so declarative
  // autoPlay on every slide left swiped-to videos stuck on a grey box.
  useEffect(() => {
    Object.entries(videoRefs.current).forEach(([i, v]) => {
      if (!v) return;
      if (Number(i) === index && videoAutoplay) {
        const p = v.play?.();
        if (p && p.catch) p.catch(() => {});
      } else {
        try { v.pause?.(); } catch { /* media not ready */ }
      }
    });
  }, [index, videoAutoplay, safe.length]);

  if (safe.length === 0) {
    return <div className={styles.placeholder}>📦</div>;
  }

  const showPeek = peekHint && !hintDone && safe.length > 1;
  /* WHO GETS ARROWS, AND WHY THE DEFAULT IS THE CONTROLLED-NESS OF THE INDEX.
   *
   * Six render sites, and they partition exactly along the line the fix needs.
   * CONTROLLED (`index` + `onIndexChange`) — `ProductMediaViewer` (its own
   * prev/next), `ProductDetail` (parent owns wheel + drag + keys + a thumbnail
   * rail) and `PropertyStay` (`hero-nav--prev/next`): every one already has
   * desktop navigation, and a second pair inside would step the index twice.
   * UNCONTROLLED — the shop grid card, `ShopProductStrip`'s card and
   * `RecMiniCard`: none has any, and all three sit inside a `<Link>`.
   *
   * So the default lands the arrows on exactly the three that lack them and
   * provably cannot reach the three that would double-step, with no call-site
   * edits. The prop stays explicit so a future controlled card can ask. */
  const showDesktopNav = (desktopNav ?? !isControlled) && safe.length > 1 && !hideOverlays;
  const isRtl = detectRtl();
  // With a single image there's nothing to swipe between, so the carousel must
  // NOT capture horizontal gestures — otherwise it swallows the drag (and
  // `touch-action: pan-y`) and the parent product strip can't scroll. When
  // static we drop the swipe handlers and let the browser pan natively.
  const interactive = safe.length > 1;
  // The dot strip mirrors in RTL (active dot moves leftward as you advance),
  // so the image track must mirror too — otherwise the picture slides the
  // opposite way to the dots. In RTL the slides lay out right-to-left and the
  // track translates the other direction.
  const sign = isRtl ? 1 : -1;

  /* THE FIRST PHOTO AND THE LAST ARE ENDS, NOT A RING.
   *
   * This used to wrap. Seen live on 2026-09-17 on a phone, on the 29-photo luggage set a Meta ad
   * sends shoppers to: a backward swipe on photo 1 jumped to 29/29 with the track racing across
   * every photo to get there — and in Hebrew a backward swipe is the finger travelling right to
   * left, the gesture an English reader makes for "next", so the owner read it as the swipe
   * running backwards. The direction was never wrong. A shopper past an end now stays where they
   * are: the swipe snaps back (`dragDx` returns to 0 below) and the arrows do nothing. Autoplay is
   * a slideshow the owner turns on, not a shopper's move, and keeps going round. */
  const lastIndex = safe.length - 1;
  const goManual = (nextIndex, action) => {
    const target = Math.min(lastIndex, Math.max(0, nextIndex));
    if (target === index) return;
    setIndex(target);
    if (onShopperMove) onShopperMove(target, action);
  };
  /** Which way a finger travelling `dx` pages, in the reading direction. */
  const stepOfDrag = (dx) => (isRtl ? (dx > 0 ? 1 : -1) : swipeDeltaToIndexStep(dx));

  const idleTouch = { x: null, y: null, w: 0, tracking: false, axis: null };
  const handleTouchStart = (e) => {
    const t = e.touches?.[0];
    if (!t) return;
    // A finger on the screen's edge is the phone's, not this carousel's (see `touchStartsAtScreenEdge`).
    const viewportWidth = typeof window !== 'undefined'
      ? (window.innerWidth || document.documentElement?.clientWidth || 0)
      : 0;
    if (touchStartsAtScreenEdge(t.clientX, viewportWidth)) {
      touchStart.current = idleTouch;
      return;
    }
    touchStart.current = {
      x: t.clientX,
      y: t.clientY,
      w: wrapRef.current?.clientWidth || 0,
      tracking: true,
      axis: null,
    };
  };

  const handleTouchMove = (e) => {
    const start = touchStart.current;
    if (!start.tracking) return;
    const t = e.touches?.[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    // The gesture is locked to one axis by its first six pixels and stays locked: a swipe that is horizontal
    // when it starts is a swipe of ours to the end, however the finger wanders — and the native listener above
    // keeps the page from scrolling under it. One that starts vertical is the page's, and the track lets go.
    if (!start.axis) {
      if (Math.abs(dx) > 6 && Math.abs(dx) > Math.abs(dy)) start.axis = 'x';
      else if (Math.abs(dy) > 6 && Math.abs(dy) > Math.abs(dx)) start.axis = 'y';
    }
    if (start.axis === 'x') {
      // Past an end there is no photo to pull in: the track gives a little and no more.
      const next = index + stepOfDrag(dx);
      setDragDx(next < 0 || next > lastIndex ? dx * EDGE_PULL : dx);
    } else if (start.axis === 'y') {
      start.tracking = false;
      setDragDx(0);
    }
  };
  touchMoveRef.current = interactive ? handleTouchMove : null;

  const handleTouchEnd = (e) => {
    const start = touchStart.current;
    if (!start.tracking) { setDragDx(0); return; }
    const endX = e.changedTouches?.[0]?.clientX ?? start.x;
    const dx = endX - start.x;
    // Commit to the next/prev slide if the user moved at least ~25% of
    // the card width OR > 30px. RTL flips the step direction so a
    // visual-left swipe still advances in reading order.
    const w = start.w || 1;
    const threshold = Math.min(w * 0.25, 40);
    if (Math.abs(dx) > threshold) {
      // Reading-direction swipe: LTR pull-left advances; RTL pull-right
      // advances — so the slide always moves the same way as the dots.
      e.stopPropagation();
      goManual(index + stepOfDrag(dx), 'swipe');
    }
    setDragDx(0);
    touchStart.current = idleTouch;
  };

  /* The OS took the gesture (an in-app browser's edge swipe, a system alert): the finger is gone
   * without a `touchend`, and a track left following it would sit between two photographs. */
  const handleTouchCancel = () => {
    setDragDx(0);
    touchStart.current = idleTouch;
  };

  // Track offset: each slide is 100% wide; index drives the base offset
  // and live drag is added on top so the user sees the image follow
  // their finger.
  const trackStyle = {
    // Lay the slides in reading order so the track mirrors the dots in RTL.
    direction: isRtl ? 'rtl' : 'ltr',
    transform: `translate3d(calc(${sign * index * 100}% + ${dragDx}px), 0, 0)`,
    transition: dragDx === 0 ? 'transform 320ms cubic-bezier(0.32, 0.72, 0, 1)' : 'none',
  };

  return (
    <div
      ref={attachWrap}
      className={`${styles.wrap} ${transparentBg ? styles.wrapTransparent : ''} ${interactive ? '' : styles.wrapStatic}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onTouchStart={interactive ? handleTouchStart : undefined}
      /* `touchmove` is the native, non-passive listener attached above — not a prop, which React makes passive. */
      onTouchEnd={interactive ? handleTouchEnd : undefined}
      onTouchCancel={interactive ? handleTouchCancel : undefined}
      data-testid="customer-card-carousel"
      data-nav-mode={dragDx !== 0 ? 'drag' : 'auto'}
    >
      {/* THE HINT KEEPS THE READING DIRECTION. It used to drop the track's inline style while it
        * played, so the slides fell back to the stylesheet's left-to-right order and a Hebrew
        * card previewed photo 2 from the RIGHT — measured on DK Store's shop at 390px: slides at
        * -85 and +86 px mid-hint, then photo 2 at -171 px, on the left, once the hint ended. The
        * slides keep the page's order now, and the hint pulls the mirrored way in RTL. */}
      <div
        className={`${styles.track} ${showPeek ? `${styles.peekTrack} ${isRtl ? styles.peekTrackRtl : ''}` : ''}`}
        style={showPeek ? { direction: trackStyle.direction } : trackStyle}
        onAnimationEnd={showPeek ? () => setHintDone(true) : undefined}
      >
        {safe.map((img, i) => (
          <div
            key={img.id || img.url || i}
            className={`${styles.slide} ${framed ? styles.slideFramed : ''}`}
            aria-hidden={i !== index}
          >
            {isVideoUrl(img.url) ? (
              // Clips run muted + looped with NO native controls. When playable
              // (gallery + viewer) they autoplay; a tap toggles play/pause in the
              // viewer (videoTapToggle) or opens the viewer in the gallery. On
              // static card strips it's a first-frame preview with a play badge.
              <>
                <video
                  ref={(el) => { videoRefs.current[i] = el; }}
                  src={posterFrameSrc(img.url)}
                  className={`${styles.img} ${fitContain ? styles.imgContain : ''}`}
                  muted
                  loop
                  playsInline
                  preload="metadata"
                  autoPlay={videoAutoplay && i === index}
                  aria-label={i === index ? alt : undefined}
                  onClick={videoTapToggle ? (e) => {
                    /* Both halves, everywhere in this file. A click has no
                     * default action on a `<video>`, so this costs nothing
                     * here — but the rule that every handler inside a card
                     * cancels as well as stops is the one that was broken on
                     * the dots, and a file with one exception in it is a file
                     * where the next handler copies the exception. */
                    e.preventDefault();
                    e.stopPropagation();
                    const v = e.currentTarget;
                    try {
                      if (v.paused) { const p = v.play(); if (p && p.catch) p.catch(() => {}); }
                      else v.pause();
                    } catch { /* media not ready / unsupported */ }
                  } : undefined}
                />
                {!videoAutoplay && (
                  <span className={styles.playBadge} aria-hidden>
                    <FiPlay />
                  </span>
                )}
              </>
            ) : (
              <PinchZoomImage
                enabled={zoomable}
                /* ONLY THE PHOTO ON SCREEN AND ITS NEIGHBOURS ARE ASKED FOR (2026-10-01). `loading="lazy"` on a
                   slide that sits one screen to the side is no restraint: the browser's lazy margin is wider than a
                   phone, so an ad shopper on 4G downloaded eleven full-size photographs of the luggage set before the
                   first one could paint (measured: the first photo was the largest paint, at 7.5 s). A slide gets its
                   address when it is the current one or next to it, and keeps it once it has had it. */
                src={wanted.has(i) ? img.url : undefined}
                data-photo={img.url}
                srcSet={wanted.has(i) && sizes ? renditionSrcSet(img.url) : undefined}
                sizes={wanted.has(i) && sizes && renditionSrcSet(img.url) ? sizes : undefined}
                alt={i === index ? alt : ''}
                className={(() => {
                  const state = framedState(img);
                  if (state === 'sized') return `${styles.img} ${styles.imgFramed}`;
                  /* No shape of its own: the plain frame — the whole box on the plain surface, the picture contained. */
                  if (state === 'unsized') return `${styles.img} ${styles.imgContain} ${styles.imgUnsized}`;
                  /* Not sized yet: HIDDEN, never `contain` in a full box with the blur behind it (fix round 3). */
                  if (state === 'pending') return `${styles.img} ${styles.imgContain} ${styles.imgPending}`;
                  return `${styles.img} ${fitContain ? styles.imgContain : ''}`;
                })()}
                style={(() => {
                  const box = framedState(img) === 'sized' ? boxOf(img) : null;
                  return box ? { width: `${box.width}px`, height: `${box.height}px` } : undefined;
                })()}
                data-framed={framedState(img)}
                loading={i === 0 ? 'eager' : 'lazy'}
                fetchpriority={i === 0 ? 'high' : undefined}
                onLoad={(framed || i === 0) ? (e) => {
                  noteRatio(img, e.currentTarget);
                  if (i === 0) wantNeighbours();
                } : undefined}
                onError={framed ? () => markUnsized(img) : undefined}
                draggable={false}
              />
            )}
          </div>
        ))}
      </div>

      {/* Hover-only, and only where a pointer is fine — `@media (hover: hover)
        * and (pointer: fine)`, the idiom `shopProductStrip.module.css` already
        * uses, so a phone never renders them and the thumb keeps the card to
        * itself. Autoplay needs no new rule: `hovered` already pauses it, and
        * these only exist while hovered.
        *
        * `preventDefault` BEFORE `stopPropagation`, and both. Measured: inside
        * an `<a>`, `stopPropagation` alone still navigates — it stops the event
        * travelling, it does not cancel the anchor's default action. That is
        * the bug this carousel already shipped on its dots. */}
      {showDesktopNav && (
        <>
          {/* `aria-disabled`, never `disabled`, at an end: a disabled button hands its click to
            * nothing, and the card's link under it would take a shopper who pressed "previous"
            * once too often into the product. */}
          <button
            type="button"
            className={`${styles.navArrow} ${styles.navPrev}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); goManual(index - 1, 'arrow'); }}
            aria-label={t('productDetail.previousPhoto', { defaultValue: 'Previous photo' })}
            aria-disabled={index <= 0}
            data-testid="customer-card-prev"
          >
            <FiChevronLeft aria-hidden />
          </button>
          <button
            type="button"
            className={`${styles.navArrow} ${styles.navNext}`}
            onClick={(e) => { e.preventDefault(); e.stopPropagation(); goManual(index + 1, 'arrow'); }}
            aria-label={t('productDetail.nextPhoto', { defaultValue: 'Next photo' })}
            aria-disabled={index >= lastIndex}
            data-testid="customer-card-next"
          >
            <FiChevronRight aria-hidden />
          </button>
        </>
      )}

      {safe.length > 1 && counter && !hideOverlays && (
        <div className={styles.counter} data-testid="customer-card-counter" aria-hidden>
          <span>{index + 1}</span>
          <span className={styles.counterSep}>/</span>
          <span>{safe.length}</span>
        </div>
      )}

      {safe.length > 1 && !counter && !hideOverlays && (
        <div
          className={styles.dots}
          role="tablist"
          aria-label={t('productDetail.galleryNav', { defaultValue: 'Image navigation' })}
        >
          {computeVisibleDots(safe.length, index).map(({ i, edge }) => (
            <button
              key={i}
              type="button"
              role="tab"
              aria-selected={i === index}
              aria-label={t('productDetail.imageNofM', { defaultValue: 'Image {{index}} of {{total}}', index: i + 1, total: safe.length })}
              data-edge={edge}
              data-testid={`customer-card-dot-${i}`}
              className={`${styles.dot} ${i === index ? styles.dotActive : ''}`}
              /* `preventDefault` FIRST, and it is the half that was missing.
               * The dot is `button < div.dots < div.wrap < … < a[href]`, and
               * `stopPropagation` cannot cancel an ancestor anchor's default
               * action. Measured in `he` and `en`: the click changed nothing
               * and `location.pathname` became `/dk-store/p/…`. The like
               * button on the same card has had the correct form all along. */
              onClick={(e) => { e.preventDefault(); e.stopPropagation(); goManual(i, 'thumb'); }}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export function computeVisibleDots(total, activeIndex) {
  if (total <= 0) return [];
  if (total <= MAX_VISIBLE_DOTS) {
    return Array.from({ length: total }, (_, i) => ({
      i,
      edge: i === activeIndex ? 'active' : Math.abs(i - activeIndex) === 1 ? 'near' : 'far',
    }));
  }
  const half = Math.floor(MAX_VISIBLE_DOTS / 2);
  let start = Math.max(0, activeIndex - half);
  const end = Math.min(total - 1, start + MAX_VISIBLE_DOTS - 1);
  start = Math.max(0, end - MAX_VISIBLE_DOTS + 1);
  const out = [];
  for (let i = start; i <= end; i++) {
    const dist = Math.abs(i - activeIndex);
    out.push({ i, edge: dist === 0 ? 'active' : dist === 1 ? 'near' : 'far' });
  }
  return out;
}

export default ProductCarousel;
